/**
 * Tip Calculator - Pure Functions for Tip Counting and Allocation
 *
 * This module contains pure functions extracted from deckLayoutUtils.ts
 * to enable unit testing without EditorState or other complex dependencies.
 *
 * Key design principles:
 * - All functions are pure (no side effects, same input = same output)
 * - No dependencies on EditorState, Vue refs, or DOM
 * - Uses simple data types that can be easily constructed in tests
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Pipetting pattern types that determine how tips are consumed
 */
export type PipettingPattern = "1x1" | "8x1" | "1x12" | "8x12" | "unknown";

/**
 * Volume-based tip option for resolving tip types based on volume
 */
export interface VolumeBasedTipOption {
    tipTypeId: string;
    volumeMin?: number;
    volumeMax?: number;
    unit?: string;
}

/**
 * Labware metadata containing selection information
 * This is a simplified interface for testing - the actual metadata
 * can have more properties but these are what we need for tip counting
 */
export interface LabwareSelectionMetadata {
    selectedRows?: number;
    selectedColumns?: number;
    selectedStartWellAddresses?: [number, number][];
}

/**
 * Labware type configuration (simplified for tip calculation)
 */
export interface LabwareTypeConfig {
    id: string;
    label?: string;
    onePerProtocol?: boolean;
    trackTipCapacity?: boolean;
}

/**
 * Step configuration (simplified for tip calculation)
 */
export interface StepConfig {
    name: string;
    id: string;
    compatiblePipettingPatterns?: string[];
    params?: Array<{
        name: string;
        role?: string;
        type?: string;
        options?: string[];
        volumeOptions?: VolumeBasedTipOption[];
    }>;
}

/**
 * A single step's tip assignment data
 * This is extracted from the document/protocol and used for tip allocation
 */
export interface StepTipAssignment {
    stepIndex: number;
    sourceId: string;
    tipTypeId: string;
    transferCount: number;
    columnsUsed: number;
    pattern: PipettingPattern;
}

/**
 * Result of calculating tips from a step
 */
export interface StepTipInfo {
    tipTypeId: string;
    sourceId: string;
    transferCount: number;
    columnsUsed: number;
    pattern: PipettingPattern;
    dispenseFromBottom: boolean;
}

/**
 * Per-step tip usage for display on the tip allocation screen.
 * This is the raw number of tips a step consumes, independent of which tip
 * type is selected (tray exclusion and per-type totals are applied by the UI).
 */
export interface StepTipUsage {
    /** Total individual tips consumed, or null when unknown (e.g. a CSV step with no stored count). */
    tips: number | null;
    /** The step's pipetting pattern (determines the columns/rows annotation). */
    pattern: PipettingPattern;
    /** Raw unit count: wells for 1x1, columns for 8x1, rows for 1x12. */
    unitCount: number;
}

/**
 * Formats a step's tip usage for display, e.g. "24 tips (3 columns)", "12 tips",
 * or "—" when the count is unknown. For multi-channel patterns the column/row
 * count is appended; single-channel shows just the tip count.
 */
export function formatTipUsage(usage: StepTipUsage): string {
    if (usage.tips === null) return "—";
    const tipWord = usage.tips === 1 ? "tip" : "tips";
    const tipsLabel = `${usage.tips} ${tipWord}`;
    if (usage.pattern === "8x1") {
        const unit = usage.unitCount === 1 ? "column" : "columns";
        return `${tipsLabel} (${usage.unitCount} ${unit})`;
    }
    if (usage.pattern === "1x12") {
        const unit = usage.unitCount === 1 ? "row" : "rows";
        return `${tipsLabel} (${usage.unitCount} ${unit})`;
    }
    return tipsLabel;
}

/**
 * Result of the tip allocation algorithm
 */
export interface TipAllocationResult {
    /** Map of tipTypeId -> array of tipbox instance IDs */
    tipboxesByType: Map<string, string[]>;
    /** Map of stepIndex -> assigned tipbox ID */
    stepToTipboxMap: Map<number, string>;
    /** Total tips needed by type */
    totalTipsByType: Map<string, number>;
}

/**
 * Generated tip labware with allocated instances
 */
export interface GeneratedTipLabware {
    id: string;
    typeId: string;
    label: string;
}

// ============================================================================
// Constants
// ============================================================================

/** A standard tipbox has 12 columns (96 tips = 12 cols × 8 tips) */
export const TIPBOX_COLUMN_CAPACITY = 12;

/** A standard tipbox has 8 rows (96 tips = 8 rows × 12 tips) */
export const TIPBOX_ROW_CAPACITY = 8;

/** Total tips in a tipbox */
export const TIPBOX_TOTAL_CAPACITY = 96;

// ============================================================================
// Pure Functions - Pattern Detection
// ============================================================================

/**
 * Determines the pipetting pattern from step config
 * @param stepConfig The step configuration
 * @returns The pipetting pattern type
 */
export function getPipettingPattern(stepConfig: StepConfig): PipettingPattern {
    if (
        !stepConfig.compatiblePipettingPatterns ||
        stepConfig.compatiblePipettingPatterns.length === 0
    ) {
        return "unknown";
    }
    const patterns = stepConfig.compatiblePipettingPatterns;

    // Check for 96-channel first (these are excluded from tip counting)
    if (patterns.includes("8x12") || patterns.includes("96-channel")) {
        return "8x12";
    }
    // Check for 8-channel (column operations)
    if (patterns.includes("8x1") || patterns.includes("8-channel")) {
        return "8x1";
    }
    // Check for 12-channel (row operations)
    if (patterns.includes("1x12") || patterns.includes("12-channel")) {
        return "1x12";
    }
    // Check for 1-channel (single tip operations)
    if (patterns.includes("1x1") || patterns.includes("1-channel")) {
        return "1x1";
    }

    return "unknown";
}

// ============================================================================
// Pure Functions - Tip Type Resolution
// ============================================================================

/**
 * Resolves the appropriate tip type based on volume and volume-based tip options
 * @param volume The volume value to match against
 * @param volumeOptions Array of volume-based tip options
 * @param fallbackTipType Fallback tip type if no volume match is found
 * @returns The resolved tip type ID
 */
export function resolveTipTypeFromVolume(
    volume: number,
    volumeOptions: VolumeBasedTipOption[],
    fallbackTipType: string
): string {
    // Find the first matching volume range
    for (const option of volumeOptions) {
        const { volumeMin } = option;

        // Check if volume is above the minimum. We don't check for the maximum, because we allow multidispensing.
        const minMatch = volumeMin === undefined || volume >= volumeMin;

        if (minMatch) {
            return option.tipTypeId;
        }
    }

    // Return fallback if no match found
    return fallbackTipType;
}

/**
 * Resolves all appropriate tip types based on volume and volume-based tip options
 * @param volume The volume value to match against
 * @param volumeOptions Array of volume-based tip options
 * @param fallbackTipType Fallback tip type if no volume match is found
 * @returns Array of all matching tip type IDs
 */
export function resolveTipTypesFromVolume(
    volume: number,
    volumeOptions: VolumeBasedTipOption[],
    fallbackTipType: string
): string[] {
    const matchingTipTypes: string[] = [];

    // Find all matching volume ranges
    for (const option of volumeOptions) {
        const { volumeMin } = option;

        // Check if volume is above the minimum. We don't check for the maximum, because we allow multidispensing.
        const minMatch = volumeMin === undefined || volume >= volumeMin;

        if (minMatch) {
            matchingTipTypes.push(option.tipTypeId);
        }
    }

    // Return all matching tip types (all that satisfy the volume minimum requirement)
    if (matchingTipTypes.length > 0) {
        return matchingTipTypes;
    }

    // Return fallback if no match found
    return [fallbackTipType];
}

// ============================================================================
// Pure Functions - Labware Config Helpers
// ============================================================================

/**
 * Helper to check if a labware type is marked as one-per-protocol in config
 * This controls whether tips are SHARED across steps (vs each step getting dedicated tips)
 */
export function isOnePerProtocol(
    typeId: string,
    labwareTypes: LabwareTypeConfig[]
): boolean {
    const lw = labwareTypes.find((t) => t.id === typeId);
    return !!lw?.onePerProtocol;
}

/**
 * Helper to check if a labware type should track tip capacity (column-wise usage)
 * This is separate from onePerProtocol to allow shared tips WITHOUT capacity tracking (Felix)
 */
export function shouldTrackTipCapacity(
    typeId: string,
    labwareTypes: LabwareTypeConfig[]
): boolean {
    const lw = labwareTypes.find((t) => t.id === typeId);
    return !!lw?.trackTipCapacity;
}

// ============================================================================
// Pure Functions - Transfer Count Extraction
// ============================================================================

/**
 * Extracts the count of unique wells/columns/rows from metadata based on pipetting pattern
 * @param metadata The labware metadata containing selection info
 * @param pattern The pipetting pattern (determines how to count)
 * @returns The number of tips needed (e.g., columns for 8x1, rows for 1x12, wells for 1x1)
 */
export function extractTransferCountFromMetadata(
    metadata: LabwareSelectionMetadata | null | undefined,
    pattern: PipettingPattern
): number {
    if (!metadata) return 1;

    switch (pattern) {
        case "8x1": {
            // 8-channel: count unique columns from selectedStartWellAddresses
            // Each address is [row, column], we count unique columns
            if (
                Array.isArray(metadata.selectedStartWellAddresses) &&
                metadata.selectedStartWellAddresses.length > 0
            ) {
                const uniqueColumns = new Set(
                    metadata.selectedStartWellAddresses.map(
                        (addr: [number, number]) => addr[1]
                    )
                );
                return uniqueColumns.size || 1;
            }
            // Fallback to selectedColumns if addresses not present
            if (
                typeof metadata.selectedColumns === "number" &&
                metadata.selectedColumns > 0
            ) {
                return metadata.selectedColumns;
            }
            return 1;
        }

        case "1x12": {
            // 12-channel: count unique rows from selectedStartWellAddresses
            // Each address is [row, column], we count unique rows
            if (
                Array.isArray(metadata.selectedStartWellAddresses) &&
                metadata.selectedStartWellAddresses.length > 0
            ) {
                const uniqueRows = new Set(
                    metadata.selectedStartWellAddresses.map(
                        (addr: [number, number]) => addr[0]
                    )
                );
                return uniqueRows.size || 1;
            }
            // Fallback to selectedRows if addresses not present
            if (
                typeof metadata.selectedRows === "number" &&
                metadata.selectedRows > 0
            ) {
                return metadata.selectedRows;
            }
            return 1;
        }

        case "1x1": {
            // 1-channel: count total unique wells from selectedStartWellAddresses
            if (
                Array.isArray(metadata.selectedStartWellAddresses) &&
                metadata.selectedStartWellAddresses.length > 0
            ) {
                // Each unique [row, column] pair is a separate tip
                return metadata.selectedStartWellAddresses.length;
            }
            // Fallback: use rows × columns for grid selection
            const rows =
                typeof metadata.selectedRows === "number" ? metadata.selectedRows : 1;
            const cols =
                typeof metadata.selectedColumns === "number"
                    ? metadata.selectedColumns
                    : 1;
            return rows * cols || 1;
        }

        default:
            return 1;
    }
}

/**
 * Converts transfer count to "columns used" in tipbox based on pattern
 * This normalizes different patterns to a common unit for capacity tracking
 * @param transferCount The number of transfers (wells/columns/rows)
 * @param pattern The pipetting pattern
 * @returns The equivalent columns used in the tipbox
 */
export function transferCountToColumnsUsed(
    transferCount: number,
    pattern: PipettingPattern
): number {
    switch (pattern) {
        case "8x1":
            // 8-channel: each transfer uses 1 column (8 tips)
            return transferCount;

        case "1x12":
            // 12-channel: each transfer uses 1.5 columns worth of tips (12 tips = 1.5 × 8)
            // Round up to ensure we don't undercount
            return Math.ceil((transferCount * 12) / 8);

        case "1x1":
            // 1-channel: need to count individual tips, convert to columns
            // Each column has 8 tips, so divide by 8 and round up
            return Math.ceil(transferCount / 8);

        default:
            return transferCount;
    }
}

/**
 * Calculates the actual number of tips needed for a given transfer based on pattern
 * @param transferCount The number of transfers (wells/columns/rows)
 * @param pattern The pipetting pattern
 * @returns The actual number of individual tips consumed
 */
export function calculateTipsNeeded(
    transferCount: number,
    pattern: PipettingPattern
): number {
    switch (pattern) {
        case "1x1":
            // 1-channel: transferCount is already individual tips
            return transferCount;
        case "8x1":
            // 8-channel: transferCount is columns, each column = 8 tips
            return transferCount * 8;
        case "1x12":
            // 12-channel: transferCount is rows, each row = 12 tips
            return transferCount * 12;
        default:
            return transferCount;
    }
}

// ============================================================================
// Pure Functions - Tip Allocation Algorithm
// ============================================================================

/**
 * Calculates total tips required per tip type from step assignments
 * This is order-independent - it sums up all tips needed regardless of step order
 *
 * @param assignments Array of step tip assignments
 * @param labwareTypes Labware type configurations for checking onePerProtocol/trackCapacity
 * @returns Map of tipTypeId -> total tips needed
 */
export function calculateTotalTipsRequired(
    assignments: StepTipAssignment[],
    labwareTypes: LabwareTypeConfig[]
): Map<string, number> {
    const totalTipsByType = new Map<string, number>();

    for (const assignment of assignments) {
        // Only process shared tip types with capacity tracking
        const isShared = isOnePerProtocol(assignment.tipTypeId, labwareTypes);
        const trackCap = shouldTrackTipCapacity(assignment.tipTypeId, labwareTypes);

        if (isShared && trackCap) {
            const current = totalTipsByType.get(assignment.tipTypeId) || 0;
            const tipsNeeded = calculateTipsNeeded(
                assignment.transferCount,
                assignment.pattern
            );
            totalTipsByType.set(assignment.tipTypeId, current + tipsNeeded);
        }
    }

    return totalTipsByType;
}

/**
 * Allocates tipboxes based on total tips needed (order-independent)
 * @param totalTipsByType Map of tipTypeId -> total tips needed
 * @param tipboxCapacity Capacity of each tipbox (default 96)
 * @param startingIdCounter Starting counter for generating tipbox IDs
 * @returns Object with tipbox allocations and the next ID counter
 */
export function allocateTipboxesForTotalUsage(
    totalTipsByType: Map<string, number>,
    tipboxCapacity: number = TIPBOX_TOTAL_CAPACITY,
    startingIdCounter: number = 0
): {
    tipboxesByType: Map<string, string[]>;
    nextIdCounter: number;
} {
    const tipboxesByType = new Map<string, string[]>();
    let idCounter = startingIdCounter;

    totalTipsByType.forEach((totalTips, tipTypeId) => {
        const numTipboxes = Math.ceil(totalTips / tipboxCapacity);
        const tipboxIds: string[] = [];

        for (let i = 0; i < numTipboxes; i++) {
            idCounter++;
            tipboxIds.push(`tips-${idCounter}`);
        }

        tipboxesByType.set(tipTypeId, tipboxIds);
    });

    return { tipboxesByType, nextIdCounter: idCounter };
}

/**
 * Assigns steps to tipboxes by sequentially filling tipboxes
 * Steps are sorted with 1ch patterns first to consolidate them into fewer tipboxes.
 *
 * @param assignments Array of step tip assignments
 * @param tipboxesByType Map of tipTypeId -> array of tipbox IDs
 * @param labwareTypes Labware type configurations
 * @returns Map of stepIndex -> assigned tipbox ID
 */
export function assignStepsToTipboxes(
    assignments: StepTipAssignment[],
    tipboxesByType: Map<string, string[]>,
    labwareTypes: LabwareTypeConfig[]
): Map<number, string> {
    const stepToTipboxMap = new Map<number, string>();

    // Group assignments by tip type
    const assignmentsByTipType = new Map<string, StepTipAssignment[]>();
    for (const assignment of assignments) {
        const existing = assignmentsByTipType.get(assignment.tipTypeId) || [];
        existing.push(assignment);
        assignmentsByTipType.set(assignment.tipTypeId, existing);
    }

    assignmentsByTipType.forEach((typeAssignments, tipTypeId) => {
        const tipboxIds = tipboxesByType.get(tipTypeId);
        if (!tipboxIds || tipboxIds.length === 0) return;

        // Sort: 1-channel patterns first to consolidate them into fewer tipboxes.
        // This helps heuristics that check "is 1ch touched" (which has stricter rules)
        // by keeping 1ch "contamination" contained to the minimal number of boxes.
        const sortedAssignments = [...typeAssignments].sort((a, b) => {
            const aIs1ch = a.pattern === "1x1";
            const bIs1ch = b.pattern === "1x1";
            if (aIs1ch && !bIs1ch) return -1;
            if (!aIs1ch && bIs1ch) return 1;
            return a.stepIndex - b.stepIndex; // Stable sort for same pattern
        });

        let currentBox = 0;
        let tipsUsedInCurrentBox = 0; // Track individual tips, not columns
        let partialColumnTips = 0; // Tips used in the current partial column (for 1ch)
        let hasPartialColumn = false; // Whether current box has a partial column

        for (const assignment of sortedAssignments) {
            const is1ch = assignment.pattern === "1x1";

            // Calculate actual tips needed based on pattern
            const tipsNeeded = calculateTipsNeeded(
                assignment.transferCount,
                assignment.pattern
            );

            if (is1ch) {
                // 1-channel: can use partial columns
                let tipsToAllocate = tipsNeeded;

                // First, try to fill the current partial column
                if (hasPartialColumn && partialColumnTips > 0) {
                    const tipsAvailableInPartialColumn = 8 - partialColumnTips;
                    const tipsFromPartialColumn = Math.min(
                        tipsToAllocate,
                        tipsAvailableInPartialColumn
                    );
                    partialColumnTips += tipsFromPartialColumn;
                    tipsUsedInCurrentBox += tipsFromPartialColumn;
                    tipsToAllocate -= tipsFromPartialColumn;

                    // If we filled the partial column, it's no longer partial
                    if (partialColumnTips === 8) {
                        partialColumnTips = 0;
                        hasPartialColumn = false;
                    }
                }

                // If we still need more tips, check if they fit in the current box
                if (tipsToAllocate > 0) {
                    const tipsAvailableInBox =
                        TIPBOX_TOTAL_CAPACITY - tipsUsedInCurrentBox;

                    if (tipsToAllocate > tipsAvailableInBox) {
                        // Move to next tipbox
                        currentBox++;
                        tipsUsedInCurrentBox = 0;
                        partialColumnTips = 0;
                        hasPartialColumn = false;
                    }

                    // Allocate remaining tips
                    tipsUsedInCurrentBox += tipsToAllocate;

                    // Update partial column tracking
                    const newPartialTips = tipsToAllocate % 8;
                    if (newPartialTips > 0) {
                        partialColumnTips = newPartialTips;
                        hasPartialColumn = true;
                    }
                }
            } else {
                // 8-channel or 12-channel: needs full, untouched columns
                const columnsNeeded = assignment.columnsUsed;

                // Calculate available full columns in current box
                const tipsAvailableInBox =
                    TIPBOX_TOTAL_CAPACITY - tipsUsedInCurrentBox;
                const fullColumnsAvailable = Math.floor(tipsAvailableInBox / 8);

                if (columnsNeeded > fullColumnsAvailable) {
                    // Move to next tipbox
                    currentBox++;
                    tipsUsedInCurrentBox = 0;
                    partialColumnTips = 0;
                    hasPartialColumn = false;
                }

                // Allocate full columns
                tipsUsedInCurrentBox += columnsNeeded * 8;
            }

            // Assign this step to the current tipbox
            const tipboxId = tipboxIds[Math.min(currentBox, tipboxIds.length - 1)];
            stepToTipboxMap.set(assignment.stepIndex, tipboxId);
        }
    });

    return stepToTipboxMap;
}

/**
 * Complete tip allocation algorithm - calculates tipboxes and assigns steps
 *
 * @param assignments Array of step tip assignments
 * @param labwareTypes Labware type configurations
 * @returns Complete allocation result with tipboxes and step mappings
 */
export function allocateTips(
    assignments: StepTipAssignment[],
    labwareTypes: LabwareTypeConfig[]
): TipAllocationResult {
    // Step 1: Calculate total tips needed per type
    const totalTipsByType = calculateTotalTipsRequired(assignments, labwareTypes);

    // Step 2: Allocate tipboxes based on total tips
    const { tipboxesByType } = allocateTipboxesForTotalUsage(totalTipsByType);

    // Step 3: Assign steps to tipboxes
    const stepToTipboxMap = assignStepsToTipboxes(
        assignments,
        tipboxesByType,
        labwareTypes
    );

    return {
        tipboxesByType,
        stepToTipboxMap,
        totalTipsByType,
    };
}

// ============================================================================
// Pure Functions - Tip Labware Generation
// ============================================================================

/**
 * Generates tip labware objects from allocation results
 *
 * @param tipboxesByType Map of tipTypeId -> array of tipbox IDs
 * @param labwareTypes Labware type configurations (for getting labels)
 * @returns Array of generated tip labware objects
 */
export function generateTipLabware(
    tipboxesByType: Map<string, string[]>,
    labwareTypes: LabwareTypeConfig[]
): GeneratedTipLabware[] {
    const tipLabware: GeneratedTipLabware[] = [];
    const tipLabelCounts = new Map<string, number>();

    tipboxesByType.forEach((tipboxIds, tipTypeId) => {
        const lwType = labwareTypes.find((t) => t.id === tipTypeId);
        const baseLabel = lwType?.label ?? tipTypeId;

        for (const tipboxId of tipboxIds) {
            const nextCount = (tipLabelCounts.get(baseLabel) || 0) + 1;
            tipLabelCounts.set(baseLabel, nextCount);

            tipLabware.push({
                id: tipboxId,
                typeId: tipTypeId,
                label: `${baseLabel} ${nextCount}`,
            });
        }
    });

    return tipLabware;
}
